import React from 'react';
import { PaginatedCourses } from '../../types/profile';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BilingualTitle } from '../common/BilingualTitle';

interface ProfileCoursesProps {
    courses: PaginatedCourses;
    hasPublishedCourses: boolean;
    onLoadMore?: () => void;
    loading?: boolean;
}

const ProfileCourses: React.FC<ProfileCoursesProps> = ({
    courses,
    hasPublishedCourses,
    onLoadMore,
    loading = false,
}) => {
    const { t } = useTranslation('profile');

    const hasMore = courses.page < courses.totalPages;

    // CEFR level colors
    const getCefrColor = (level?: string): string => {
        switch (level?.toUpperCase()) {
            case 'A1':
                return 'bg-green-100 text-green-700';
            case 'A2':
                return 'bg-green-200 text-green-800';
            case 'B1':
                return 'bg-blue-100 text-blue-700';
            case 'B2':
                return 'bg-blue-200 text-blue-800';
            case 'C1':
                return 'bg-purple-100 text-purple-700';
            case 'C2':
                return 'bg-purple-200 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                    {hasPublishedCourses
                        ? t('courses.created', 'Created Courses')
                        : t('courses.enrolled', 'Enrolled Courses')}
                </h2>
                <span className="text-sm text-gray-500">
                    {courses.total} {t('courses.total', 'total')}
                </span>
            </div>

            {courses.items.length === 0 ? (
                <div className="text-center py-8">
                    <svg
                        className="mx-auto h-12 w-12 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">
                        {hasPublishedCourses
                            ? t('courses.noCreated', 'No courses created yet')
                            : t('courses.noEnrolled', 'No courses enrolled yet')}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {courses.items.map((course) => (
                            <Link
                                key={course.id}
                                to={`/courses/${course.id}`}
                                className="group block bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                            >
                                {/* Thumbnail */}
                                <div className="aspect-video bg-gray-200 relative">
                                    {course.thumbnailUrl ? (
                                        <img
                                            src={course.thumbnailUrl}
                                            alt={course.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <svg
                                                className="w-12 h-12 text-gray-300"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                    {course.cefrLevel && (
                                        <span
                                            className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${getCefrColor(course.cefrLevel)}`}
                                        >
                                            {course.cefrLevel}
                                        </span>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-3">
                                    <BilingualTitle
                                        title={course.title}
                                        titles={course.titles}
                                        variant="card"
                                        as="h3"
                                        className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors"
                                    />
                                    {course.description && (
                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                            {course.description}
                                        </p>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Load More */}
                    {hasMore && (
                        <div className="text-center mt-6">
                            <button
                                onClick={onLoadMore}
                                disabled={loading}
                                className="px-4 py-2 text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                            >
                                {loading
                                    ? t('common:loading', 'Loading...')
                                    : t('common:loadMore', 'Load More')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ProfileCourses;
